import { createEffect, createMemo, createSignal } from "solid-js";
import { type useLocation, useNavigate, useSearch } from "@tanstack/solid-router";
import type { HabitRow } from "@habits/shared";

import { readInitialSearchParam } from "@/lib/initial-search";

/**
 * Typed partial patch of layout overlay search-params. Other
 * params (`day`, `habit`, …) are preserved via `...prev`. Literal types maintain
 * `"habit"`/`"delete"`/`"shortcuts"` required by TanStack's strict typing.
 */
type OverlaySearch = {
  new?: "habit";
  edit?: string;
  confirm?: "delete";
  modal?: "shortcuts";
};

const mergeOverlaySearch = (patch: OverlaySearch) => (prev: Record<string, unknown>) => ({
  ...prev,
  ...patch,
});

/**
 * State of global shell overlays, addressable via URL (#15b).
 *
 * Dialogs that can open from any screen (new/edit habit,
 * delete habit, shortcuts modal) live in search-params of `_app` layout
 * (`?new=habit`, `?edit=<id>`, `?edit=<id>&confirm=delete`, `?modal=shortcuts`),
 * this way each state is copyable/reloadable. Pattern #9: synchronous seed from
 * initial URL + URL→state reflection via `useSearch()`; "open/close" navigates
 * with `replace:true` to avoid history inflation.
 *
 * `editingHabit` is resolved from habit list (permission-aware): an
 * `?edit=<id>` that does not appear after sync = nonexistent/forbidden; the
 * dialog is not forced open in that case (habits view provides feedback).
 */
export function useAppDialogs(
  location: ReturnType<typeof useLocation>,
  habitsById: () => Map<string, HabitRow>,
) {
  const navigate = useNavigate();
  const search = useSearch({ strict: false });

  // Synchronous seed (direct load from pasted link).
  const initialNew = readInitialSearchParam("new") === "habit";
  const initialEdit = readInitialSearchParam("edit");
  const initialModal = readInitialSearchParam("modal");

  const [isSidebarOpen, setIsSidebarOpen] = createSignal(false);

  // ── Habit dialog (new / edit) ───────────────────────────────────────────
  // State LIVES in URL: `?new=habit` (create) or `?edit=<id>` (edit).
  const overlay = () => search() as OverlaySearch;
  const editId = createMemo<string | undefined>(() => overlay().edit);
  const wantsNew = createMemo<boolean>(() => overlay().new === "habit");

  const editingHabit = createMemo<HabitRow | null>(() => {
    const id = editId();
    if (!id) {
      return null;
    }
    return habitsById().get(id) ?? null;
  });

  // Dialog opens when URL requests creation, or requests editing a present habit.
  const habitDialogOpen = createMemo<boolean>(() => wantsNew() || editingHabit() !== null);

  // ── Delete confirmation (nested in edit) ──────────────────────────────────
  const confirmDeleteOpen = createMemo<boolean>(
    () => editingHabit() !== null && overlay().confirm === "delete",
  );

  // ── Shortcuts modal ──────────────────────────────────────────────────────
  const shortcutsOpen = createMemo<boolean>(() => overlay().modal === "shortcuts");

  // First tick seed (before `useSearch()` settles): if pasted URL
  // requests an overlay, we navigate to materialize the validated param. It is
  // idempotent (same value) and prevents losing the deep-link in deferred flush.
  createEffect(() => {
    void location().pathname;
  });
  if (initialNew || initialEdit || initialModal === "shortcuts") {
    queueMicrotask(() => {
      void navigate({
        to: ".",
        search: mergeOverlaySearch({
          new: initialNew ? "habit" : undefined,
          edit: initialEdit,
          modal: initialModal === "shortcuts" ? "shortcuts" : undefined,
        }),
        replace: true,
      });
    });
  }

  const openNewHabitDialog = () => {
    void navigate({
      to: ".",
      search: mergeOverlaySearch({ new: "habit", edit: undefined }),
      replace: true,
    });
  };

  const openEditHabitDialog = (habit: HabitRow) => {
    void navigate({
      to: ".",
      search: mergeOverlaySearch({ edit: habit.id, new: undefined }),
      replace: true,
    });
  };

  const setHabitDialogOpen = (open: boolean) => {
    if (!open) {
      void navigate({
        to: ".",
        search: mergeOverlaySearch({ new: undefined, edit: undefined, confirm: undefined }),
        replace: true,
      });
    }
  };

  const setConfirmDeleteOpen = (open: boolean) => {
    void navigate({
      to: ".",
      search: mergeOverlaySearch({ confirm: open ? "delete" : undefined }),
      replace: true,
    });
  };

  const setShortcutsOpen = (open: boolean) => {
    void navigate({
      to: ".",
      search: mergeOverlaySearch({ modal: open ? "shortcuts" : undefined }),
      replace: true,
    });
  };

  // Mobile side drawer is ephemeral navigation (N/A in audit): closes on
  // route change and does not deserve its own URL.
  createEffect(() => {
    void location().pathname;
    setIsSidebarOpen(false);
  });

  return {
    habitDialogOpen,
    setHabitDialogOpen,
    editingHabit,
    confirmDeleteOpen,
    setConfirmDeleteOpen,
    isSidebarOpen,
    setIsSidebarOpen,
    shortcutsOpen,
    setShortcutsOpen,
    openNewHabitDialog,
    openEditHabitDialog,
  };
}
